import { useState, useCallback } from 'react';
import { usePlatformAdminAuthStore } from '../../stores/platform-admin-auth.store';
import {
  auditLogsService,
  AuditLog,
  PaginatedAuditLogs,
  GetAuditLogsParams,
  AuditLogStats,
} from '../../services/platform-admin/audit-logs.service';

export function useAuditLogs() {
  const token = usePlatformAdminAuthStore((state) => state.token);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getLogs = useCallback(
    async (params?: GetAuditLogsParams): Promise<PaginatedAuditLogs | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const logs = await auditLogsService.getLogs(token, params);
        return logs;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch audit logs');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const getLogById = useCallback(
    async (id: string): Promise<AuditLog | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const log = await auditLogsService.getLogById(token, id);
        return log;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch audit log');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const getStats = useCallback(
    async (startDate?: string, endDate?: string): Promise<AuditLogStats | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const stats = await auditLogsService.getStats(token, startDate, endDate);
        return stats;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch statistics');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const getUniqueActions = useCallback(async (): Promise<string[] | null> => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    try {
      return await auditLogsService.getUniqueActions(token);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch actions');
      return null;
    }
  }, [token]);

  const getUniqueResources = useCallback(async (): Promise<string[] | null> => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    try {
      return await auditLogsService.getUniqueResources(token);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch resources');
      return null;
    }
  }, [token]);

  const getAdminActivity = useCallback(
    async (platformAdminId: string, limit?: number): Promise<AuditLog[] | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const activity = await auditLogsService.getAdminActivity(token, platformAdminId, limit);
        return activity;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch admin activity');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const getOrganizationActivity = useCallback(
    async (organizationId: string, limit?: number): Promise<AuditLog[] | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const activity = await auditLogsService.getOrganizationActivity(
          token,
          organizationId,
          limit
        );
        return activity;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch organization activity');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  return {
    isLoading,
    error,
    getLogs,
    getLogById,
    getStats,
    getUniqueActions,
    getUniqueResources,
    getAdminActivity,
    getOrganizationActivity,
  };
}
